import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../../atoms/Icon/Icon';
import { Popover, popoverAnchorClass } from '../../primitives/Popover/Popover';
import { FilterChipCounter } from '../FilterChipCounter/FilterChipCounter';
import { FilterChipMenu, type FilterChipOption } from '../FilterChipMenu/FilterChipMenu';
import styles from './FilterChip.module.css';

/**
 * Figma: `Filter Chip` (93:1707) — "A dropdown filter control for narrowing
 * content by selecting options from a multi-select menu. When selected, displays
 * a blue badge with the active selection count."
 *
 * `State` is `Default | Hover | Open` as of 2026-08-11 — the third option was
 * called `Active` until then, and the annotation still says so. Renaming it
 * changed nothing here: that axis was never a prop.
 *
 * The counter is no longer the shared `Counter` atom. The 2026-09 update
 * replaced it with `_filter chip counter` (44:521) — a blue `radius/xs` badge
 * on `component/badge/info-surface` under `text/link`, which is what the
 * variant annotations meant all along when they said "blue badge". See
 * `FilterChipCounter`.
 *
 * The same update added a leading icon: every variant's `Wrapper` now opens
 * with a 12x12 instance-swap slot (the `placeholder` symbol by default, filled
 * `icon/secondary`), `space/6` before the label. That is what took the chip
 * from 71x28 to 89x28 unselected and 90 to 108 selected. `Filters Bar` swaps in
 * one of six new dimension icons per chip.
 *
 * Neither variant property becomes an enum prop. `State=Open` is the panel being
 * open, `State=Hover` is CSS, and `Selected?` is derived from whether anything
 * is selected — a chip cannot be `Selected?=True` with an empty selection, so it
 * is not something a caller should be able to set.
 */
export type FilterChipState = 'default' | 'hover';

export type { FilterChipOption };

export type FilterChipProps = {
  /** The chip's own label, e.g. "Brand". */
  label: string;
  /**
   * Figma's leading instance-swap slot. Defaults to the `placeholder` symbol,
   * which is what the six `Filter Chip` variants themselves draw. Pass `null`
   * to render no icon at all — a case Figma does not show, since the slot is
   * always filled there.
   */
  icon?: ReactNode;
  options: FilterChipOption[];
  /** Controlled selection. Omit to let the chip own it. */
  selected?: string[];
  defaultSelected?: string[];
  onSelectionChange?: (values: string[]) => void;
  /** Controlled open state. Omit to let the chip own it. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  searchPlaceholder?: string;
  /**
   * Force a state for documentation and visual regression only. Hover is CSS in
   * the real component and Open is the `open` prop.
   */
  state?: FilterChipState;
  /** Force every menu row's hover appearance, for documentation only. */
  itemState?: 'default' | 'hover';
  className?: string;
};

export function FilterChip({
  label,
  icon = <Icon name="placeholder" />,
  options,
  selected,
  defaultSelected = [],
  onSelectionChange,
  open,
  defaultOpen = false,
  onOpenChange,
  searchPlaceholder,
  state,
  itemState,
  className,
}: FilterChipProps) {
  const panelId = useId();
  const [uncontrolledSelected, setUncontrolledSelected] = useState<string[]>(defaultSelected);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [search, setSearch] = useState('');

  const currentSelected = selected ?? uncontrolledSelected;
  const isOpen = open ?? uncontrolledOpen;

  const setSelected = useCallback(
    (values: string[]) => {
      if (selected === undefined) setUncontrolledSelected(values);
      onSelectionChange?.(values);
    },
    [selected, onSelectionChange],
  );

  const setOpen = useCallback(
    (next: boolean) => {
      if (open === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [open, onOpenChange],
  );

  const toggle = useCallback(
    (value: string, checked: boolean) => {
      setSelected(
        checked
          ? [...currentSelected, value]
          : currentSelected.filter((v) => v !== value),
      );
    },
    [currentSelected, setSelected],
  );

  /*
   * Figma `Select all`. Existing order is preserved and newly-selected values
   * are appended, so toggling the row on and off does not silently reshuffle a
   * selection the caller may be reading back.
   */
  const toggleMany = useCallback(
    (values: string[], checked: boolean) => {
      setSelected(
        checked
          ? [...currentSelected, ...values.filter((v) => !currentSelected.includes(v))]
          : currentSelected.filter((v) => !values.includes(v)),
      );
    },
    [currentSelected, setSelected],
  );

  const hasSelection = currentSelected.length > 0;

  /*
   * Figma hides the counter entirely on `Selected?=False`, and unmounting it is
   * the literal reading of that — but it also means clearing a filter destroys
   * the badge on the frame the count reaches zero, so the chip has nothing left
   * to collapse and snaps back to its narrow width. The badge stays mounted and
   * holds the last real count while its column closes over it; the collapsed
   * state is 0px wide and `inert`, so the stale number is neither visible nor
   * reachable. Both resting states still match the design exactly.
   */
  const lastCount = useRef(currentSelected.length);
  if (hasSelection) lastCount.current = currentSelected.length;

  /*
   * `State` and `Selected?` are two independent axes in Figma, each cell of the
   * 2x3 grid carrying its own surface token, so `selected` is applied alongside
   * `open`/`hover` rather than instead of them — see the stylesheet.
   *
   * Within the State axis the three are mutually exclusive, which is how Figma
   * models them: there is no Hover+Open variant, so `interactive` is withheld
   * while the panel is open and the live `:hover` rule cannot fight the open
   * surface.
   */
  const classes = [
    styles.trigger,
    hasSelection ? styles.selected : null,
    isOpen ? styles.open : state === 'hover' ? styles.hover : null,
    !isOpen && state === undefined ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={popoverAnchorClass}>
      <button
        className={classes}
        type="button"
        /*
         * Not in Figma — a disclosure needs to announce what it controls.
         *
         * No `aria-haspopup`: it used to say `"true"`, which is defined as a
         * synonym for `"menu"` and promised a menu that deliberately is not
         * here — the panel is `role="group"` because it holds checkboxes, not
         * menuitems. None of the allowed values (menu, listbox, tree, grid,
         * dialog) describes a group, so the attribute is dropped and this is
         * left as the plain disclosure pattern, which `aria-expanded` plus
         * `aria-controls` already state accurately.
         */
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={() => setOpen(!isOpen)}
      >
        {/* Figma's leading slot. Decorative: the label beside it names the
            filter, so the icon carries no information of its own. Wrapped
            rather than rendered bare so the `icon/secondary` binding reaches a
            caller's own node too — see the stylesheet. */}
        {icon === null ? null : <span className={styles.leadingIcon}>{icon}</span>}
        <span className={styles.textAndBadge}>
          <span className={styles.label}>{label}</span>
          <span className={styles.counterSlot} inert={!hasSelection}>
            <span className={styles.counterClip}>
              <span className={styles.counterInner}>
                <FilterChipCounter count={lastCount.current} label={`${label} filters`} />
              </span>
            </span>
          </span>
        </span>
        {/* One symbol, rotated — see the stylesheet for why that is the same
            picture Figma's two Open variants specify. */}
        <Icon name="chevronDown" className={styles.chevron} />
      </button>

      <Popover
        open={isOpen}
        onClose={() => setOpen(false)}
        placement="bottom-start"
        /*
         * transitions.dev menu dropdown, the same motion `Dropdown` opens with:
         * grows from the chip's corner (`bottom-start` puts the origin top-left)
         * rather than sliding, so every anchored menu in a filters bar opens alike.
         */
        motion="scale"
        /* A panel of checkboxes is not a menu — `menu` expects menuitem children. */
        role="group"
        label={label}
        id={panelId}
        className={styles.panel}
      >
        <FilterChipMenu
          options={options}
          selected={currentSelected}
          onToggle={toggle}
          onToggleMany={toggleMany}
          onClear={() => setSelected([])}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={searchPlaceholder}
          itemState={itemState}
        />
      </Popover>
    </div>
  );
}
